import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import { Map, TileLayer, Marker } from "react-leaflet";
import { debounce, cloneDeep } from "lodash";
import {
  Avatar,
  Breadcrumb,
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Switch,
  Tabs,
  Table,
  Tooltip,
  Typography,
  Upload,
  message, Select, Space, Popconfirm
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  InboxOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { SetRoute } from "../scripts/action";
import { Constants } from "../scripts/settings";
import API from "../scripts/api";
import Resources from "../scripts/resources";
import PaginationInfo from "../components/PaginationInfo";
import BrandForm from "../forms/Brand";
import QuestionForm from "../forms/Question";
import SliderForm from "../forms/Slider";
import TagForm from "../forms/Tag";
import PackageForm from "../forms/Package";
import QuestionView from "../views/Question";
import CountryForm from "../forms/Country";
import CityForm from "../forms/City";

const { Option } = Select;

class Brands extends Component {
  constructor() {
    super();

    this.state = {
      data: [],
      pagination: {
        total: 0,
        current: 1,
        pageSize: Constants.pageSize,
        size: "small",
      },
    };

    this.formRef = React.createRef();
  }

  fetch = (params = {}) => {
    this.setState({ loading: true });

    API.brand
      .list(params)
      .then((result) => {
        this.setState({
          loading: false,
          data: result.data,
          pagination: {
            ...this.state.pagination,
            ...{
              total: result.data.length,
            },
          },
        });
      })
      .catch(() => {
        this.setState({ loading: false });
      });
  };

  showForm = (brand = {}) => {
    this.setState({ visibleForm: true, selected: brand });

    setTimeout(() => {
      this.formRef.current.handleDidMount(brand);
    }, 0);
  };

  hideForm = () => {
    this.setState({ visibleForm: false });

    setTimeout(() => {
      this.formRef.current.handleWillUnmount();
    }, 0);
  };

  handleStatus = (record) => {
    Modal.confirm({
      title: record.status
        ? Resources.confirmDeactivate
        : Resources.confirmActivate,
      okText: Resources.yes,
      okType: record.status ? "danger" : "primary",
      cancelText: Resources.no,
      onOk: () => {
        this.setState({ loading: true });

        API.brand
          .statusUpdate(record)
          .then((result) => {
            message.success(result.message);
            this.fetch();
          })
          .catch(() => {
            this.setState({ loading: false });
          });
      },
    });
  };

  handleDelete = (id) => {
    Modal.confirm({
      title: Resources.confirmDelete,
      okText: Resources.yes,
      okType: "danger",
      cancelText: Resources.no,
      onOk: () => {
        this.setState({ loading: true });

        API.brand
          .delete(id)
          .then((result) => {
            message.success(result.message);
            this.fetch();
          })
          .catch(() => {
            this.setState({ loading: false });
          });
      },
    });
  };

  handleSubmit = (values) => {
    const action = this.state.selected.id ? API.brand.update : API.brand.add;

    this.setState({ submitting: true });

    action(values, this.state.selected.id)
      .then((result) => {
        message.success(result.message);
        this.setState({ submitting: false });
        this.hideForm();
        this.fetch();
      })
      .catch(() => {
        this.setState({ submitting: false });
      });
  };

  handleTable = (pagination) => {
    this.setState({
      pagination: {
        ...this.state.pagination,
        ...{ current: pagination.current },
      },
    });
  };

  componentDidMount() {
    this.fetch();
  }

  render() {
    const { data, loading, pagination, submitting, visibleForm } = this.state;

    const columns = [
      {
        title: Resources.image,
        dataIndex: "image",
        render: (val) => (
          <Tooltip
            title={
              <Avatar
                src={`${Constants.imagePathBrand}${val}`}
                shape="square"
                size={200}
              />
            }
          >
            <Avatar
              src={`${Constants.imagePathBrand}${val}`}
              shape="square"
              size="small"
            />
          </Tooltip>
        ),
      },
      {
        title: `${Resources.firstName} ${Resources.brand}`,
        dataIndex: "name",
        className: "col-center",
      },
      {
        title: Resources.barcode,
        dataIndex: "code",
        className: "col-center",
      },
      {
        title: Resources.status,
        dataIndex: "status",
        className: "col-center",
        width: "70px",
        render: (val, record) => (
          <Switch
            size="small"
            checked={!!val}
            onClick={() => this.handleStatus(record)}
          />
        ),
      },
      {
        title: Resources.action,
        dataIndex: "id",
        className: "col-center",
        width: "120px",
        render: (val, record) => (
          <Fragment>
            <Tooltip title={Resources.edit}>
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={() => this.showForm(record)}
              />
            </Tooltip>
            <Tooltip title={Resources.delete}>
              <Button
                type="link"
                icon={<DeleteOutlined />}
                onClick={() => this.handleDelete(val)}
              />
            </Tooltip>
          </Fragment>
        ),
      },
    ];

    return (
      <Fragment>
        <Row gutter={24} style={{ marginBottom: 16 }}>
          <Col xs={24} md={4}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => this.showForm()}
              block
            >
              {Resources.add}
            </Button>
          </Col>
        </Row>
        <Table
          columns={columns}
          dataSource={data}
          loading={loading}
          onChange={this.handleTable}
          pagination={pagination}
          size="small"
          rowKey="id"
          scroll={{ x: 600 }}
        />
        {!!pagination.total && (
          <PaginationInfo
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            style={{ bottom: 0, right: 0 }}
          />
        )}
        <BrandForm
          ref={this.formRef}
          onCancel={this.hideForm}
          onSubmit={this.handleSubmit}
          submitting={submitting}
          visible={visibleForm}
        />
      </Fragment>
    );
  }
}

class Questions extends Component {
  constructor() {
    super();

    this.state = {
      data: [],
      pagination: {
        total: 0,
        current: 1,
        pageSize: Constants.pageSize,
        size: "small",
      },
      selected: {},
    };

    this.formRef = React.createRef();
  }

  fetch = (params = {}) => {
    this.setState({ loading: true });

    API.question
      .list(params)
      .then((result) => {
        this.setState({
          loading: false,
          data: result.data,
          pagination: {
            ...this.state.pagination,
            ...{
              total: result.data.length,
            },
          },
        });
      })
      .catch(() => {
        this.setState({ loading: false });
      });
  };

  showForm = (question = {}) => {
    this.setState({ visibleForm: true, selected: question });

    setTimeout(() => {
      this.formRef.current.handleDidMount(question);
    }, 0);
  };

  hideForm = () => {
    this.setState({ visibleForm: false });

    setTimeout(() => {
      this.formRef.current.handleWillUnmount();
    }, 0);
  };

  showView = (question = {}) => {
    this.setState({ visibleView: true, selected: question });
  };

  hideView = () => {
    this.setState({ visibleView: false });
  };

  handleDelete = (id) => {
    Modal.confirm({
      title: Resources.confirmDelete,
      okText: Resources.yes,
      okType: "danger",
      cancelText: Resources.no,
      onOk: () => {
        this.setState({ loading: true });

        API.question
          .delete(id)
          .then((result) => {
            message.success(result.message);
            this.fetch();
          })
          .catch(() => {
            this.setState({ loading: false });
          });
      },
    });
  };

  handleSubmit = (values) => {
    const action = this.state.selected.id
      ? API.question.update
      : API.question.add;

    this.setState({ submitting: true });

    action(values, this.state.selected.id)
      .then((result) => {
        message.success(result.message);
        this.setState({ submitting: false });
        this.hideForm();
        this.fetch();
      })
      .catch(() => {
        this.setState({ submitting: false });
      });
  };

  handleTable = (pagination) => {
    this.setState({
      pagination: {
        ...this.state.pagination,
        ...{ current: pagination.current },
      },
    });
  };

  componentDidMount() {
    this.fetch();
  }

  render() {
    const {
      data,
      loading,
      pagination,
      selected,
      submitting,
      visibleForm,
      visibleView,
    } = this.state;

    const columns = [
      {
        title: Resources.question,
        dataIndex: "question",
        render: (val) => (
          <Typography.Paragraph ellipsis={{ rows: 1 }} style={{ margin: 0 }}>
            {val}
          </Typography.Paragraph>
        ),
      },
      {
        title: Resources.answer,
        dataIndex: "answer",
        render: (val) => (
          <Typography.Paragraph ellipsis={{ rows: 1 }} style={{ margin: 0 }}>
            {val}
          </Typography.Paragraph>
        ),
      },
      {
        title: Resources.type,
        dataIndex: "type",
        render: (val) => {
          switch (val) {
            case "1":
              return `${Resources.questions} ${Resources.public}`;
            case "2":
              return `${Resources.questions} ${Resources.buy}`;
            case "3":
              return `${Resources.questions} ${Resources.payment}`;
            default:
              return "-";
          }
        },
      },
      {
        title: Resources.action,
        dataIndex: "id",
        className: "col-center",
        width: "120px",
        render: (val, record) => (
          <Fragment>
            <Tooltip title={Resources.view}>
              <Button
                type="link"
                icon={<EyeOutlined />}
                onClick={() => this.showView(record)}
              />
            </Tooltip>
            <Tooltip title={Resources.edit}>
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={() => this.showForm(record)}
              />
            </Tooltip>
            <Tooltip title={Resources.delete}>
              <Button
                type="link"
                icon={<DeleteOutlined />}
                onClick={() => this.handleDelete(val)}
              />
            </Tooltip>
          </Fragment>
        ),
      },
    ];

    return (
      <Fragment>
        <Row gutter={24} style={{ marginBottom: 16 }}>
          <Col xs={24} md={4}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => this.showForm()}
              block
            >
              {Resources.add}
            </Button>
          </Col>
        </Row>
        <Table
          columns={columns}
          dataSource={data}
          loading={loading}
          onChange={this.handleTable}
          pagination={pagination}
          size="small"
          rowKey="id"
          scroll={{ x: 600 }}
        />
        {!!pagination.total && (
          <PaginationInfo
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            style={{ bottom: 0, right: 0 }}
          />
        )}
        <QuestionForm
          ref={this.formRef}
          onCancel={this.hideForm}
          onSubmit={this.handleSubmit}
          submitting={submitting}
          visible={visibleForm}
        />
        <QuestionView
          data={selected}
          visible={visibleView}
          onClose={this.hideView}
        />
      </Fragment>
    );
  }
}

class Sliders extends Component {
  constructor() {
    super();

    this.state = {
      categories: [],
      data: [],
      pagination: {
        total: 0,
        current: 1,
        pageSize: Constants.pageSize,
        size: "small",
      },
      tags: [],
    };

    this.formRef = React.createRef();
  }

  fetch = (params = {}) => {
    this.setState({ loading: true });

    API.slider
      .list(params)
      .then((result) => {
        if (result.data) {
          this.setState({
            loading: false,
            data: result.data.data,
            pagination: {
              ...this.state.pagination,
              ...{
                current: result.data.current_page,
                pageSize: result.data.per_page,
                total: result.data.total,
              },
            },
          });
        } else {
          this.setState({ loading: false });
        }
      })
      .catch(() => {
        this.setState({ loading: false });
      });
  };

  showForm = (slider = {}) => {
    this.setState({ visibleForm: true, selected: slider });

    setTimeout(() => {
      this.formRef.current.handleDidMount(slider);
    }, 0);
  };

  hideForm = () => {
    this.setState({ visibleForm: false });

    setTimeout(() => {
      this.formRef.current.handleWillUnmount();
    }, 0);
  };

  handleStatus = (record) => {
    Modal.confirm({
      title: record.status
        ? Resources.confirmDeactivate
        : Resources.confirmActivate,
      okText: Resources.yes,
      okType: record.status ? "danger" : "primary",
      cancelText: Resources.no,
      onOk: () => {
        this.setState({ loading: true });

        API.slider
          .statusUpdate(record)
          .then((result) => {
            message.success(result.message);
            this.fetch({ page: this.state.pagination.current });
          })
          .catch(() => {
            this.setState({ loading: false });
          });
      },
    });
  };

  handleDelete = (id) => {
    Modal.confirm({
      title: Resources.confirmDelete,
      okText: Resources.yes,
      okType: "danger",
      cancelText: Resources.no,
      onOk: () => {
        this.setState({ loading: true });

        API.slider
          .delete(id)
          .then((result) => {
            message.success(result.message);
            this.fetch({ page: this.state.pagination.current });
          })
          .catch(() => {
            this.setState({ loading: false });
          });
      },
    });
  };

  handleSubmit = (values) => {
    const action = this.state.selected.id ? API.slider.update : API.slider.add;

    this.setState({ submitting: true });

    action(values, this.state.selected.id)
      .then((result) => {
        message.success(result.message);
        this.setState({ submitting: false });
        this.hideForm();
        this.fetch({ page: this.state.pagination.current });
      })
      .catch(() => {
        this.setState({ submitting: false });
      });
  };

  handleTable = (pagination) => {
    this.fetch({ page: pagination.current });
  };

  componentDidMount() {
    this.fetch({ page: 1 });

    API.tag.list().then((result) => {
      this.setState({ tags: result.data });
    });

    API.category.list().then((result) => {
      this.setState({ categories: result.data });
    });
  }

  render() {
    const {
      categories,
      data,
      loading,
      pagination,
      submitting,
      tags,
      visibleForm,
    } = this.state;

    const columns = [
      {
        title: Resources.image,
        dataIndex: "image",
        render: (val) => (
          <Tooltip
            title={
              <Avatar
                src={`${Constants.imagePathSlider}${val}`}
                shape="square"
                size={200}
              />
            }
          >
            <Avatar
              src={`${Constants.imagePathSlider}${val}`}
              shape="square"
              size="small"
            />
          </Tooltip>
        ),
      },
      {
        title: `${Resources.category} / ${Resources.tag} / ${Resources.link}`,
        dataIndex: "value",
        className: "col-center",
        render: (val, record) => {
          switch (record.type) {
            case "category":
              const category = categories.find((o) => o.id === parseInt(val));
              return category ? category.name : "-";
            case "tag":
              const tag = tags.find((o) => o.id === parseInt(val));
              return tag ? tag.name : "-";
            default:
              return (
                <span style={{ display: "block", direction: "ltr" }}>
                  {val}
                </span>
              );
          }
        },
      },
      {
        title: Resources.type,
        dataIndex: "type",
        className: "col-center",
        render: (val) => Resources[val],
      },
      {
        title: Resources.location,
        dataIndex: "location",
        className: "col-center",
        render: (val) => Resources[val],
      },
      {
        title: Resources.device,
        dataIndex: "device",
        className: "col-center",
        render: (val) => Resources[val] || "-",
      },
      {
        title: Resources.status,
        dataIndex: "status",
        className: "col-center",
        width: "70px",
        render: (val, record) => (
          <Switch
            size="small"
            checked={!!val}
            onClick={() => this.handleStatus(record)}
          />
        ),
      },
      {
        title: Resources.action,
        dataIndex: "id",
        className: "col-center",
        width: "120px",
        render: (val, record) => (
          <Fragment>
            <Tooltip title={Resources.edit}>
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={() => this.showForm(record)}
              />
            </Tooltip>
            <Tooltip title={Resources.delete}>
              <Button
                type="link"
                icon={<DeleteOutlined />}
                onClick={() => this.handleDelete(val)}
              />
            </Tooltip>
          </Fragment>
        ),
      },
    ];

    return (
      <Fragment>
        <Row gutter={24} style={{ marginBottom: 16 }}>
          <Col xs={24} md={4}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => this.showForm()}
              block
            >
              {Resources.add}
            </Button>
          </Col>
        </Row>
        <Table
          columns={columns}
          dataSource={data}
          loading={loading}
          onChange={this.handleTable}
          pagination={pagination}
          size="small"
          rowKey="id"
          scroll={{ x: 600 }}
        />
        {!!pagination.total && (
          <PaginationInfo
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            style={{ bottom: 0, right: 0 }}
          />
        )}
        <SliderForm
          ref={this.formRef}
          onCancel={this.hideForm}
          onSubmit={this.handleSubmit}
          submitting={submitting}
          visible={visibleForm}
          categories={categories}
          tags={tags}
        />
      </Fragment>
    );
  }
}

class Tags extends Component {
  constructor() {
    super();

    this.state = {
      data: [],
      pagination: {
        total: 0,
        current: 1,
        pageSize: Constants.pageSize,
        size: "small",
      },
    };

    this.formRef = React.createRef();

    this.handleSearch = debounce(this.handleSearch, 1000);
  }

  fetch = (params = {}) => {
    this.setState({ loading: true });

    params.q = this.state.filterSearch;

    API.tag
      .list(params)
      .then((result) => {
        if (result.data) {
          this.setState({
            loading: false,
            data: result.data.data,
            pagination: {
              ...this.state.pagination,
              ...{
                current: result.data.current_page,
                pageSize: result.data.per_page,
                total: result.data.total,
              },
            },
          });
        } else {
          this.setState({ loading: false });
        }
      })
      .catch(() => {
        this.setState({ loading: false });
      });
  };

  showForm = (tag = {}) => {
    this.setState({ visibleForm: true, selected: tag });

    setTimeout(() => {
      this.formRef.current.handleDidMount(tag);
    }, 0);
  };

  hideForm = () => {
    this.setState({ visibleForm: false });
    this.formRef.current.handleWillUnmount();
  };

  handleSearch = (value) => {
    this.setState({ filterSearch: value });

    setTimeout(() => {
      this.fetch({ page: 1 });
    }, 0);
  };

  handleDelete = (id) => {
    Modal.confirm({
      title: Resources.confirmDelete,
      okText: Resources.yes,
      okType: "danger",
      cancelText: Resources.no,
      onOk: () => {
        this.setState({ loading: true });

        API.tag
          .delete(id)
          .then((result) => {
            message.success(result.message);
            this.fetch({ page: this.state.pagination.current });
          })
          .catch(() => {
            this.setState({ loading: false });
          });
      },
    });
  };

  handleSubmit = (values) => {
    const action = this.state.selected.id ? API.tag.update : API.tag.add;

    this.setState({ submitting: true });

    action(values, this.state.selected.id)
      .then((result) => {
        message.success(result.message);
        this.setState({ submitting: false });
        this.hideForm();
        this.fetch({ page: this.state.pagination.current });
      })
      .catch(() => {
        this.setState({ submitting: false });
      });
  };

  componentDidMount() {
    this.fetch({ page: 1 });
  }

  render() {
    const { data, loading, pagination, submitting, visibleForm } = this.state;

    const columns = [
      {
        title: Resources.title,
        dataIndex: "name",
      },
      {
        title: Resources.action,
        dataIndex: "id",
        className: "col-center",
        width: "120px",
        render: (val, record) => (
          <Fragment>
            <Tooltip title={Resources.edit}>
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={() => this.showForm(record)}
              />
            </Tooltip>
            <Tooltip title={Resources.delete}>
              <Button
                type="link"
                icon={<DeleteOutlined />}
                onClick={() => this.handleDelete(val)}
              />
            </Tooltip>
          </Fragment>
        ),
      },
    ];

    return (
      <Fragment>
        <Row gutter={24} style={{ marginBottom: 16 }}>
          <Col xs={24} md={4}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => this.showForm()}
              block
            >
              {Resources.add}
            </Button>
          </Col>
          <Col xs={24} md={{ span: 6, offset: 14 }}>
            <Tooltip title={Resources.search}>
              <Input
                type="text"
                prefix={<SearchOutlined />}
                placeholder={Resources.search}
                onChange={({ target: { value } }) => this.handleSearch(value)}
                allowClear
              />
            </Tooltip>
          </Col>
        </Row>
        <Table
          columns={columns}
          dataSource={data}
          loading={loading}
          onChange={this.handleTableChange}
          pagination={pagination}
          size="small"
          rowKey="id"
          scroll={{ x: 600 }}
        />
        {!!pagination.total && (
          <PaginationInfo
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            style={{ bottom: 0, right: 0 }}
          />
        )}
        <TagForm
          ref={this.formRef}
          onCancel={this.hideForm}
          onSubmit={this.handleSubmit}
          submitting={submitting}
          visible={visibleForm}
        />
      </Fragment>
    );
  }
}
class Package extends Component {
  constructor() {
    super();

    this.state = {
      data: [],
      pagination: {
        total: 0,
        current: 1,
        pageSize: Constants.pageSize,
        size: "small",
      },
    };

    this.formRef = React.createRef();

    this.handleSearch = debounce(this.handleSearch, 1000);
  }

  fetch = (params = {}) => {
    this.setState({ loading: true });

    params.q = this.state.filterSearch;

    API.package
      .list(params)
      .then((result) => {
        if (result.data) {
          this.setState({
            loading: false,
            data: result.data.data,
            pagination: {
              ...this.state.pagination,
              ...{
                current: result.data.current_page,
                pageSize: result.data.per_page,
                total: result.data.total,
              },
            },
          });
        } else {
          this.setState({ loading: false });
        }
      })
      .catch(() => {
        this.setState({ loading: false });
      });
  };

  showForm = (tag = {}) => {
    this.setState({ visibleForm: true, selected: tag });

    setTimeout(() => {
      this.formRef.current.handleDidMount(tag);
    }, 0);
  };

  hideForm = () => {
    this.setState({ visibleForm: false });
    this.formRef.current.handleWillUnmount();
  };

  handleSearch = (value) => {
    this.setState({ filterSearch: value });

    setTimeout(() => {
      this.fetch({ page: 1 });
    }, 0);
  };

  handleDelete = (id) => {
    Modal.confirm({
      title: Resources.confirmDelete,
      okText: Resources.yes,
      okType: "danger",
      cancelText: Resources.no,
      onOk: () => {
        this.setState({ loading: true });

        API.package
          .delete(id)
          .then((result) => {
            message.success(result.message);
            this.fetch({ page: this.state.pagination.current });
          })
          .catch(() => {
            this.setState({ loading: false });
          });
      },
    });
  };

  handleSubmit = (values) => {
    const action = this.state.selected.id ? API.package.update : API.package.add;

    this.setState({ submitting: true });

    action(values, this.state.selected.id)
      .then((result) => {
        message.success(result.message);
        this.setState({ submitting: false });
        this.hideForm();
        this.fetch({ page: this.state.pagination.current });
      })
      .catch(() => {
        this.setState({ submitting: false });
      });
  };

  componentDidMount() {
    this.fetch({ page: 1 });
  }

  render() {
    const { data, loading, pagination, submitting, visibleForm } = this.state;

    const columns = [
      {
        title: Resources.title,
        dataIndex: "title",
      },
      {
        title: Resources.count,
        dataIndex: "count",
      },
      {
        title: Resources.action,
        dataIndex: "id",
        className: "col-center",
        width: "120px",
        render: (val, record) => (
          <Fragment>
            <Tooltip title={Resources.edit}>
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={() => this.showForm(record)}
              />
            </Tooltip>
            <Tooltip title={Resources.delete}>
              <Button
                type="link"
                icon={<DeleteOutlined />}
                onClick={() => this.handleDelete(val)}
              />
            </Tooltip>
          </Fragment>
        ),
      },
    ];

    return (
      <Fragment>
        <Row gutter={24} style={{ marginBottom: 16 }}>
          <Col xs={24} md={4}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => this.showForm()}
              block
            >
              {Resources.add}
            </Button>
          </Col>
          <Col xs={24} md={{ span: 6, offset: 14 }}>
            <Tooltip title={Resources.search}>
              <Input
                type="text"
                prefix={<SearchOutlined />}
                placeholder={Resources.search}
                onChange={({ target: { value } }) => this.handleSearch(value)}
                allowClear
              />
            </Tooltip>
          </Col>
        </Row>
        <Table
          columns={columns}
          dataSource={data}
          loading={loading}
          onChange={this.handleTableChange}
          pagination={pagination}
          size="small"
          rowKey="id"
          scroll={{ x: 600 }}
        />
        {!!pagination.total && (
          <PaginationInfo
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            style={{ bottom: 0, right: 0 }}
          />
        )}
        <PackageForm
          ref={this.formRef}
          onCancel={this.hideForm}
          onSubmit={this.handleSubmit}
          submitting={submitting}
          visible={visibleForm}
        />
      </Fragment>
    );
  }
}

class SettingsPage extends Component {
  constructor() {
    super();

    this.state = {
      activeTab: "1",
      data: {},
    };

    this.formRankRef = React.createRef();
    this.formShareRef = React.createRef();
    this.formTermsRef = React.createRef();
    this.formContactRef = React.createRef();
    this.formTransportRef = React.createRef();
    this.formCountryRef = React.createRef();
    this.formCityRef = React.createRef();
  }

  fetch = () => {
    this.setState({ loading: true });

    API.setting
      .list()
      .then((result) => {
        if (result.data.phones) {
          if (result.data.phones.length > 0)
            result.data.phoneOne = result.data.phones[0];

          if (result.data.phones.length > 1)
            result.data.phoneTwo = result.data.phones[1];

          if (result.data.phones.length > 2)
            result.data.phoneThree = result.data.phones[2];

          if (result.data.phones.length > 3)
            result.data.phoneFour = result.data.phones[3];
        }

        this.setState({ loading: false, data: result.data });
      })
      .catch(() => {
        this.setState({ loading: false });
      });
  };

  handleMap = (e = {}) => {
    this.formContactRef.current.setFieldsValue({
      lat: e.latlng.lat,
      lng: e.latlng.lng,
    });
  };

  handleTabChange = (tab) => {
    this.setState({ activeTab: tab });

    setTimeout(() => {
      const data = cloneDeep(this.state.data);
      switch (tab) {
        case "4":
          this.formRankRef.current.setFieldsValue(data);
          break;
        case "6":
          this.formShareRef.current.setFieldsValue(data);
          break;
        case "7":
          this.formTermsRef.current.setFieldsValue(data);
          break;
        case "8":
          this.formContactRef.current.setFieldsValue(data);
          break;
        case "9":
          this.formTransportRef.current.setFieldsValue(data);
          break;

          
        default:
          break;
      }
    }, 0);
  };

  handleSubmit = (values) => {
    this.setState({ submitting: true });

    const formData = new FormData();

    switch (this.state.activeTab) {
      case "4":
        formData.append("goldenRank", values.goldenRank);
        formData.append("silverRank", values.silverRank);
        formData.append("bronzeRank", values.bronzeRank);
        formData.append("scoreText", values.scoreText);
        break;
      case "6":
        formData.append("inviteText", values.inviteText);
        if (
          values.inviteImage &&
          values.inviteImage.fileList &&
          values.inviteImage.fileList.length
        )
          formData.append(
            "inviteImage",
            values.image.file,
            values.image.file.name
          );
        break;
      case "7":
        formData.append("rules", values.rules);
        break;
      case "8":
        if (values.phoneOne) formData.append("phones[]", values.phoneOne);
        if (values.phoneTwo) formData.append("phones[]", values.phoneTwo);
        if (values.phoneThree) formData.append("phones[]", values.phoneThree);
        if (values.phoneFour) formData.append("phones[]", values.phoneFour);

        formData.append("supportPhone", values.supportPhone);
        formData.append("supportPhone", values.supportPhone);
        formData.append("websiteLink", values.websiteLink);
        formData.append("mailAddress", values.mailAddress);
        formData.append("telegramId", values.telegramId);
        formData.append("instagramId", values.instagramId);
        formData.append("address", values.address);
        formData.append("lat", values.lat);
        formData.append("lng", values.lng);
        break;
      case "9":
        formData.append("freeShipping", values.freeShipping);
        formData.append("service", values.service);
        formData.append("costService", values.costService);
        break;

      case "10":
        formData.append("name", values.name);
        formData.append("code", values.code);
        if (
          values.inviteImage &&
          values.inviteImage.fileList &&
          values.inviteImage.fileList.length
        )
          formData.append(
            "image",
            values.image.file,
            values.image.file.name
          );
        break;
      default:
        break;
    }

    API.setting
      .update(formData)
      .then((result) => {
        message.success(result.message);
        this.setState({ submitting: false });
        this.fetch();
      })
      .catch(() => {
        this.setState({ submitting: false });
      });
  };

  componentDidMount() {
    const { onSetRoute, routeName, parentName } = this.props;

    onSetRoute({ openKey: parentName, selectedKey: routeName });

    this.fetch();
  }

  render() {
    const { activeTab, data, inviteImageList, loading, submitting } =
      this.state;

    const props = {
      onRemove: (file) => {
        this.setState({ inviteImageList: [] });
      },
      beforeUpload: (file) => {
        this.setState({ inviteImageList: [file] });
        return false;
      },
      showUploadList: {
        showRemoveIcon: false,
      },
      fileList: inviteImageList || [],
    };

    return (
      <Fragment>
        <Breadcrumb>
          <Breadcrumb.Item>{Resources.settings}</Breadcrumb.Item>
        </Breadcrumb>
        <div className="ant-content">
          <Tabs
            activeKey={activeTab}
            type="card"
            onTabClick={this.handleTabChange}
          >
            <Tabs.TabPane tab={Resources.slider} key="1" disabled={loading}>
              <Sliders />
            </Tabs.TabPane>
            <Tabs.TabPane tab={Resources.offBrands} key="2" disabled={loading}>
              <Brands />
            </Tabs.TabPane>
            <Tabs.TabPane tab={Resources.tags} key="3" disabled={loading}>
              <Tags />
            </Tabs.TabPane>
            <Tabs.TabPane tab={Resources.package} key="20" disabled={loading}>
              <Package />
            </Tabs.TabPane>
            <Tabs.TabPane tab={Resources.faq} key="5" disabled={loading}>
              <Questions />
            </Tabs.TabPane>
            <Tabs.TabPane
              tab={Resources.shareFriends}
              key="6"
              disabled={loading}
            >
              <Form
                ref={this.formShareRef}
                onFinish={this.handleSubmit}
                layout="vertical"
              >
                <Row gutter={24}>
                  <Col xs={24} md={16}>
                    <Form.Item name="inviteText" label={Resources.text}>
                      <Input.TextArea rows={6} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item name="inviteImage" label={Resources.image}>
                      <Upload.Dragger {...props}>
                        <p className="ant-upload-drag-icon">
                          <InboxOutlined />
                        </p>
                        <p className="ant-upload-hint">
                          {Resources.uploadText}
                        </p>
                      </Upload.Dragger>
                      {data.inviteImage && (
                        <img
                          src={`${Constants.imagePathSetting}${data.inviteImage}`}
                          alt=""
                          style={{
                            display: "block",
                            marginTop: 16,
                            width: "100%",
                          }}
                        />
                      )}
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={{ span: 4, offset: 20 }}>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={submitting}
                      block
                    >
                      {Resources.record}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Tabs.TabPane>
            <Tabs.TabPane tab={Resources.terms} key="7" disabled={loading}>
              <Form
                ref={this.formTermsRef}
                onFinish={this.handleSubmit}
                layout="vertical"
              >
                <Row gutter={24}>
                  <Col xs={24}>
                    <Form.Item name="rules" label={Resources.text}>
                      <Input.TextArea rows={6} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={{ span: 4, offset: 20 }}>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={submitting}
                      block
                    >
                      {Resources.record}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Tabs.TabPane>
            <Tabs.TabPane
              tab={Resources.contactInfo}
              key="8"
              disabled={loading}
            >
              <Form
                ref={this.formContactRef}
                onFinish={this.handleSubmit}
                layout="vertical"
              >
                <Row gutter={24}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="phoneOne"
                      label={`${Resources.phone} ${Resources.one}`}
                    >
                      <Input type="text" className="ant-input-ltr" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="phoneTwo"
                      label={`${Resources.phone} ${Resources.two}`}
                    >
                      <Input type="text" className="ant-input-ltr" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="phoneThree"
                      label={`${Resources.phone} ${Resources.three}`}
                    >
                      <Input type="text" className="ant-input-ltr" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="phoneFour" label="واتساپ">
                      <Input type="text" className="ant-input-ltr" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="supportPhone"
                      label={`${Resources.phone} ${Resources.support}`}
                    >
                      <Input type="text" className="ant-input-ltr" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="websiteLink"
                      label={`${Resources.web} ${Resources.site}`}
                    >
                      <Input type="text" className="ant-input-ltr" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="mailAddress" label={Resources.email}>
                      <Input type="text" className="ant-input-ltr" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="telegramId" label={Resources.telegram}>
                      <Input type="text" className="ant-input-ltr" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="instagramId" label={Resources.instagram}>
                      <Input type="text" className="ant-input-ltr" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="address" label={Resources.address}>
                      <Input type="text" />
                    </Form.Item>
                  </Col>
                  <Col xs={12}>
                    <Form.Item name="lat" label={Resources.latitude}>
                      <Input type="text" className="ant-input-ltr" disabled />
                    </Form.Item>
                  </Col>
                  <Col xs={12}>
                    <Form.Item name="lng" label={Resources.longitude}>
                      <Input type="text" className="ant-input-ltr" disabled />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item
                      shouldUpdate={(prevValues, currentValues) =>
                        prevValues.lat !== currentValues.lat ||
                        prevValues.lng !== currentValues.lng
                      }
                      noStyle
                    >
                      {({ getFieldValue }) => {
                        const lat =
                          getFieldValue("lat") || Constants.defaultLat;
                        const lng =
                          getFieldValue("lng") || Constants.defaultLng;

                        return (
                          <Map
                            center={{ lat: lat, lng: lng }}
                            length={4}
                            onClick={this.handleMap}
                            zoom={Constants.defaultZoom}
                            style={{
                              marginBottom: 24,
                              height: 300,
                              width: "100%",
                            }}
                          >
                            <TileLayer
                              attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                              url="http://{s}.tile.osm.org/{z}/{x}/{y}.png"
                            />
                            <Marker position={{ lat: lat, lng: lng }} />
                          </Map>
                        );
                      }}
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={{ span: 4, offset: 20 }}>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={submitting}
                      block
                    >
                      {Resources.record}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Tabs.TabPane>
            <Tabs.TabPane
              tab={Resources.transportation}
              key="9"
              disabled={loading}
            >
              <Form
                ref={this.formTransportRef}
                onFinish={this.handleSubmit}
                layout="vertical"
              >
                <Row gutter={24}>
                  <Col xs={24} md={8}>
                    <Form.Item
                      name="freeShipping"
                      label={Resources.freeShippingRadius}
                      help={Resources.basedOnMeters}
                    >
                      <InputNumber min={0} style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item
                      name="service"
                      label={Resources.costShippingRadius}
                      help={Resources.basedOnMeters}
                    >
                      <InputNumber min={0} style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item
                      name="costService"
                      label={`${Resources.costService} (${Resources.rial})`}
                    >
                      <InputNumber min={0} style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={{ span: 4, offset: 20 }}>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={submitting}
                      block
                    >
                      {Resources.record}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Tabs.TabPane>

            <Tabs.TabPane tab={Resources.country} key="10" disabled={loading}>
              <Country />
            </Tabs.TabPane>

            <Tabs.TabPane tab={Resources.city} key="11" disabled={loading}>
              <City />
            </Tabs.TabPane>

            <Tabs.TabPane tab={Resources.neighborhood} key="12" disabled={loading}>
              <Neighborhood />
            </Tabs.TabPane>
          </Tabs>
        </div>
      </Fragment>
    );
  }
}


class Country extends Component {
  constructor() {
    super();

    this.state = {
      data: [],
      pagination: {
        total: 0,
        current: 1,
        pageSize: Constants.pageSize,
        size: "small",
      },
      tags: [],
    };

    this.formRef = React.createRef();
  }

  fetch = (params = {}) => {
    this.setState({ loading: true });

    API.country
      .list(params)
      .then((result) => {
        if (result.data) {
          this.setState({
            loading: false,
            data: result.data,
            pagination: {
              ...this.state.pagination,
              ...{
                current: result.current_page,
                pageSize: result.per_page,
                total: result.total,
              },
            },
          });
        } else {
          this.setState({ loading: false });
        }
      })
      .catch(() => {
        this.setState({ loading: false });
      });
  };

  showForm = (slider = {}) => {
    this.setState({ visibleForm: true, selected: slider });

    setTimeout(() => {
      this.formRef.current.handleDidMount(slider);
    }, 0);
  };

  hideForm = () => {
    this.setState({ visibleForm: false });

    setTimeout(() => {
      this.formRef.current.handleWillUnmount();
    }, 0);
  };

 

  handleDelete = (id) => {
    Modal.confirm({
      title: Resources.confirmDelete,
      okText: Resources.yes,
      okType: "danger",
      cancelText: Resources.no,
      onOk: () => {
        this.setState({ loading: true });

        API.country
          .delete(id)
          .then((result) => {
            message.success(result.message);
            this.fetch({ page: this.state.pagination.current });
          })
          .catch(() => {
            this.setState({ loading: false });
          });
      },
    });
  };

  handleSubmit = (values) => {
    const action = this.state.selected.id ? API.country.update : API.country.add;

    this.setState({ submitting: true });

    action(values, this.state.selected.id)
      .then((result) => {
        message.success(result.message);
        this.setState({ submitting: false });
        this.hideForm();
        this.fetch({ page: this.state.pagination.current });
      })
      .catch(() => {
        this.setState({ submitting: false });
      });
  };

  handleTable = (pagination) => {
    this.fetch({ page: pagination.current });
  };

  componentDidMount() {
    this.fetch({ page: 1 });
  }

  render() {
    const {
      categories,
      data,
      loading,
      pagination,
      submitting,
      tags,
      visibleForm,
    } = this.state;

    const columns = [
      {
        title: Resources.image,
        dataIndex: "image",
        render: (val) => (
          <Tooltip
            title={
              <Avatar
                src={`${Constants.imagePathCountry}${val}`}
                shape="square"
                size={200}
              />
            }
          >
            <Avatar
              src={`${Constants.imagePathCountry}${val}`}
              shape="square"
              size="small"
            />
          </Tooltip>
        ),
      },
   
      {
        title: Resources.title,
        dataIndex: "name",
        className: "col-center",
      },
      {
        title: Resources.code,
        dataIndex: "code",
        className: "col-center",
      },
     
      {
        title: Resources.action,
        dataIndex: "id",
        className: "col-center",
        width: "120px",
        render: (val, record) => (
          <Fragment>
            <Tooltip title={Resources.edit}>
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={() => this.showForm(record)}
              />
            </Tooltip>
            <Tooltip title={Resources.delete}>
              <Button
                type="link"
                icon={<DeleteOutlined />}
                onClick={() => this.handleDelete(val)}
              />
            </Tooltip>
          </Fragment>
        ),
      },
    ];

    return (
      <Fragment>
        <Row gutter={24} style={{ marginBottom: 16 }}>
          <Col xs={24} md={4}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => this.showForm()}
              block
            >
              {Resources.add}
            </Button>
          </Col>
        </Row>
        <Table
          columns={columns}
          dataSource={data}
          loading={loading}
          onChange={this.handleTable}
          pagination={pagination}
          size="small"
          rowKey="id"
          scroll={{ x: 600 }}
        />
        {!!pagination.total && (
          <PaginationInfo
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            style={{ bottom: 0, right: 0 }}
          />
        )}
        <CountryForm
          ref={this.formRef}
          onCancel={this.hideForm}
          onSubmit={this.handleSubmit}
          submitting={submitting}
          visible={visibleForm}
        />
      </Fragment>
    );
  }
}

class City extends Component {
  constructor() {
    super();

    this.state = {
      data: [],
      pagination: {
        total: 0,
        current: 1,
        pageSize: Constants.pageSize,
        size: "small",
      },
      tags: [],
      city: 0,
      countries: [],
      selectedCountry: null
    };

    this.formRef = React.createRef();
  }

  fetch = (params = {}) => {
    this.setState({ loading: true });

    API.city
      .list(this.state.selectedCountry, params)
      .then((result) => {
        if (result.data) {
          this.setState({
            loading: false,
            data: result.data,
            pagination: {
              ...this.state.pagination,
              ...{
                current: result.current_page,
                pageSize: result.per_page,
                total: result.total,
              },
            },
          });
        } else {
          this.setState({ loading: false });
        }
      })
      .catch(() => {
        this.setState({ loading: false });
      });
  };

  
getCountries = (params = {}) => {
    API.country
      .list(params)
      .then((result) => {
        if (result.data) {
          this.setState({
            countries: result.data,
          });
        } else {
          this.setState({ loading: false });
        }
      })
      .catch(() => {
        this.setState({ loading: false });
      });
  };
  showForm = (slider = {}) => {
    this.setState({ visibleForm: true, selected: slider });

    setTimeout(() => {
      this.formRef.current.handleDidMount(slider);
    }, 0);
  };

  hideForm = () => {
    this.setState({ visibleForm: false });

    setTimeout(() => {
      this.formRef.current.handleWillUnmount();
    }, 0);
  };

 

  handleDelete = (id) => {
    Modal.confirm({
      title: Resources.confirmDelete,
      okText: Resources.yes,
      okType: "danger",
      cancelText: Resources.no,
      onOk: () => {
        this.setState({ loading: true });

        API.city
          .delete(id)
          .then((result) => {
            message.success(result.message);
            this.fetch({ page: this.state.pagination.current });
          })
          .catch(() => {
            this.setState({ loading: false });
          });
      },
    });
  };

  handleSubmit = (values) => {
    const action = this.state.selected.id ? API.city.update : API.city.add;

    this.setState({ submitting: true });

    action(values, this.state.selected.id)
      .then((result) => {
        message.success(result.message);
        this.setState({ submitting: false });
        this.hideForm();
        this.fetch({ page: this.state.pagination.current });
      })
      .catch(() => {
        this.setState({ submitting: false });
      });
  };

  handleTable = (pagination) => {
    this.fetch({ page: pagination.current });
  };

  componentDidMount() {
    this.getCountries();
  }

  handleCountryChange = async (value) => {
    console.log(value)
    this.setState({ selectedCountry: value }, async () => {
      // این کد زمانی اجرا می‌شود که state حتما آپدیت شده باشد
      await this.fetch({ page: 1 });
    });
  }

  render() {
    const {
      countries, data, loading, pagination, submitting, visibleForm,} = this.state;

    const columns = [
      {
        title: Resources.image,
        dataIndex: "image",
        render: (val) => (
          <Tooltip
            title={
              <Avatar
                src={`${Constants.imagePathCountry}${val}`}
                shape="square"
                size={200}
              />
            }
          >
            <Avatar
              src={`${Constants.imagePathCountry}${val}`}
              shape="square"
              size="small"
            />
          </Tooltip>
        ),
      },
   
      {
        title: Resources.title,
        dataIndex: "name",
        className: "col-center",
      },
     
      {
        title: Resources.action,
        dataIndex: "id",
        className: "col-center",
        width: "120px",
        render: (val, record) => (
          <Fragment>
            <Tooltip title={Resources.edit}>
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={() => this.showForm(record)}
              />
            </Tooltip>
            <Tooltip title={Resources.delete}>
              <Button
                type="link"
                icon={<DeleteOutlined />}
                onClick={() => this.handleDelete(val)}
              />
            </Tooltip>
          </Fragment>
        ),
      },
    ];

    return (
      <Fragment>
        <Row gutter={24} style={{ marginBottom: 16 }}>

          <Col xs={24} sm={10} md={6}>
            <Form.Item label={Resources.country}>
              <Select
                showSearch
                allowClear
                placeholder= {Resources.selectCountry}
                onChange={this.handleCountryChange}
                optionFilterProp="children"
                filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())}
              >
                {countries.map(c => (
                  <Select.Option key={c.id} value={c.id}>
                    {c.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>


          <Col xs={24} md={4}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => this.showForm()}
              block
            >
              {Resources.add}
            </Button>
          </Col>
        </Row>
        <Table
          columns={columns}
          dataSource={data}
          loading={loading}
          onChange={this.handleTable}
          pagination={pagination}
          size="small"
          rowKey="id"
          scroll={{ x: 600 }}
        />
        {!!pagination.total && (
          <PaginationInfo
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            style={{ bottom: 0, right: 0 }}
          />
        )}
        <CityForm
          ref={this.formRef}
          onCancel={this.hideForm}
          onSubmit={this.handleSubmit}
          submitting={submitting}
          visible={visibleForm}
        />
      </Fragment>
    );
  }
}


class Neighborhood extends Component {


   constructor() {
    super();

    this.state = {
      countries: [],
      cities: [],
      neighborhoods: [],

      selectedCountry: null,
      selectedCity: null,

      loadingCountries: false,
      loadingCities: false,
      loadingNeighborhoods: false,
      submitting: false,

      modalVisible: false,
      editingNeighborhood: null,
    };

    this.formRef = React.createRef();
  }


  componentDidMount() {
    this.fetchCountries();
  }

  // -------------------------
  // Fetch Countries
  // -------------------------
  fetchCountries = () => {
    this.setState({ loadingCountries: true });

    API.country
      .list()
      .then((res) => {
        this.setState({
          countries: res.data || [],
          loadingCountries: false,
        });
      })
      .catch(() => {
        this.setState({ loadingCountries: false });
      });
  };

  // -------------------------
  // Fetch Cities
  // -------------------------
  fetchCities = (countryId) => {
    if (!countryId) return;

    this.setState({ loadingCities: true });

    console.log("countryId", countryId)
    API.city
      .list( countryId )
      .then((res) => {
        this.setState({
          cities: res.data || [],
          loadingCities: false,
        });
      })
      .catch(() => {
        this.setState({ loadingCities: false });
      });
  };

  // -------------------------
  // Fetch Neighborhoods
  // -------------------------
  fetchNeighborhoods = (cityId) => {
    if (!cityId) return;

    this.setState({ loadingNeighborhoods: true });

    API.neighborhood
      .list(cityId )
      .then((res) => {
        this.setState({
          neighborhoods: res.data || [],
          loadingNeighborhoods: false,
        });
      })
      .catch(() => {
        this.setState({ loadingNeighborhoods: false });
      });
  };

  // -------------------------
  // Country Change
  // -------------------------
  handleCountryChange = (countryId) => {
    this.setState(
      {
        selectedCountry: countryId,
        selectedCity: null,
        cities: [],
        neighborhoods: [],
      },
      () => {
        this.fetchCities(countryId);
      }
    );
  };

  // -------------------------
  // City Change
  // -------------------------
  handleCityChange = (cityId) => {
    this.setState(
      {
        selectedCity: cityId,
        neighborhoods: [],
      },
      () => {
        this.fetchNeighborhoods(cityId);
      }
    );
  };

  // -------------------------
  // Open Modal for Add
  // -------------------------
  openAddModal = () => {
    this.setState(
      {
        modalVisible: true,
        editingNeighborhood: null,
      },
      () => {
        setTimeout(() => {
            this.formRef.current.resetFields();
            this.formRef.current.setFieldsValue({
              name: "",
            });
          
          
        }, 0);
      }
    );
  };

  // -------------------------
  // Open Modal for Edit
  // -------------------------
  openEditModal = (record) => {
    this.setState(
      {
        modalVisible: true,
        editingNeighborhood: record,
      },
      () => {
        setTimeout(() => {
          this.formRef.current.setFieldsValue({
            name: record.name,
          });
        }, 0);
      }
    );
  };

  // -------------------------
  // Close Modal
  // -------------------------
  closeModal = () => {
    this.setState({
      modalVisible: false,
      editingNeighborhood: null,
      submitting: false,
    });
  };

  // -------------------------
  // Submit Add / Edit
  // -------------------------
  handleSubmit = () => {
    const { selectedCity, editingNeighborhood } = this.state;

    this.formRef.current
      .validateFields()
      .then((values) => {
        const payload = {
          ...values,
          city_id: selectedCity,
        };

        this.setState({ submitting: true });

        const action = editingNeighborhood
          ? API.neighborhood.update
          : API.neighborhood.add;

        const request = editingNeighborhood
          ? action(editingNeighborhood.id, payload)
          : action(payload);

        request
          .then((res) => {
            message.success(res.message || "با موفقیت انجام شد");
            this.setState({ submitting: false });
            this.closeModal();
            this.fetchNeighborhoods(selectedCity);
          })
          .catch(() => {
            this.setState({ submitting: false });
          });
      });
  };

  // -------------------------
  // Delete Neighborhood
  // -------------------------
  handleDelete = (id) => {
    API.neighborhood
      .delete(id)
      .then((res) => {
        message.success(res.message || "محله حذف شد");
        this.fetchNeighborhoods(this.state.selectedCity);
      });
  };

  render() {
    const {
      countries,
      cities,
      neighborhoods,
      selectedCountry,
      selectedCity,
      loadingCountries,
      loadingCities,
      loadingNeighborhoods,
      modalVisible,
      editingNeighborhood,
      submitting,
    } = this.state;

    const columns = [
      {
        title: "نام محله",
        dataIndex: "name",
        key: "name",
      },
      {
        title: "عملیات",
        key: "actions",
        render: (_, record) => (
          <Space>
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => this.openEditModal(record)}
            >
              ویرایش
            </Button>

            <Popconfirm
              title="آیا از حذف این محله مطمئن هستید؟"
              okText="بله"
              cancelText="خیر"
              onConfirm={() => this.handleDelete(record.id)}
            >
              <Button type="link" danger icon={<DeleteOutlined />}>
                حذف
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ];

    return (
      <div>
        <Row gutter={16}>
          <Col span={8}>
            <Select
              placeholder="انتخاب کشور"
              value={selectedCountry}
              onChange={this.handleCountryChange}
              loading={loadingCountries}
              style={{ width: "100%" }}
              allowClear
            >
              {countries.map((item) => (
                <Option key={item.id} value={item.id}>
                  {item.name}
                </Option>
              ))}
            </Select>
          </Col>

          <Col span={8}>
            <Select
              placeholder="انتخاب شهر"
              value={selectedCity}
              onChange={this.handleCityChange}
              loading={loadingCities}
              style={{ width: "100%" }}
              allowClear
              disabled={!selectedCountry}
            >
              {cities.map((item) => (
                <Option key={item.id} value={item.id}>
                  {item.name}
                </Option>
              ))}
            </Select>
          </Col>

          <Col span={8}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={this.openAddModal}
              disabled={!selectedCity}
              block
            >
              افزودن محله
            </Button>
          </Col>
        </Row>

        <Table
          style={{ marginTop: 16 }}
          columns={columns}
          dataSource={neighborhoods}
          rowKey="id"
          loading={loadingNeighborhoods}
          pagination={false}
          locale={{
            emptyText: selectedCity
              ? "محله‌ای یافت نشد"
              : "ابتدا کشور و شهر را انتخاب کنید",
          }}
        />

        <Modal
          title={editingNeighborhood ? "ویرایش محله" : "افزودن محله"}
          open={modalVisible}
          onCancel={this.closeModal}
          onOk={this.handleSubmit}
          confirmLoading={submitting}
          okText="ثبت"
          cancelText="انصراف"
          destroyOnClose
        >
          <Form ref={this.formRef} layout="vertical">
            <Form.Item
              label="نام محله"
              name="name"
              rules={[{ required: true, message: "نام محله الزامی است" }]}
            >
              <Input placeholder="نام محله را وارد کنید" />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    );
  }
}


export default connect(null, (dispatch) => {
  return {
    onSetRoute: (data) => dispatch(SetRoute(data)),
  };
})(SettingsPage);
